import { minorUnits } from "./money";
import type {
  PurchaseOrderDraft,
  PurchaseOrderLine,
  TaxGroup,
} from "../api/purchase_orders";
import type { StockyPublicBigIntId } from "../api/stocky_public_ids";

/**
 * 発注書ドラフトの一時編集（この発注書限り・永続しない）。
 *
 * cost_override（仕入単価の手入力）だけはサーバ永続なので PATCH で別管理し、
 * ここでは数量・行除外・ヘッダ項目のみを扱う。編集を適用した「実効ドラフト」を
 * 集計カード・PDF・CSV へ渡すことで、税区分別小計と税額を編集後の値で再計算する。
 */
export interface LineEdit {
  /** この発注書限りの発注数量。未指定なら recommended_qty を使う。 */
  quantity?: number;
  /** true ならこの明細を発注書から除外する。 */
  excluded?: boolean;
}

/** variant_id → 明細の一時編集。 */
export type DraftEdits = Record<string, LineEdit>;

/**
 * `applyDraftEdits` の出力。編集適用後は発注数量が確定し、明細金額・税区分別集計が
 * 算出済みになるため、生の API 型で null になりうる予測フィールドが非 null に確定する
 * （手動注文書でも数量入力後は必ず数値になる）。PDF/CSV/費用サマリーはこの確定済み
 * ドラフトだけを受け取る。
 */
export interface EffectivePurchaseOrderLine extends Omit<
  PurchaseOrderLine,
  "recommended_qty" | "line_amount"
> {
  recommended_qty: number;
  line_amount: string;
}

export interface EffectivePurchaseOrderDraft extends Omit<
  PurchaseOrderDraft,
  "lines" | "tax_groups" | "total_amount"
> {
  lines: EffectivePurchaseOrderLine[];
  tax_groups: TaxGroup[];
  total_amount: string;
}

/** 発注書ヘッダの編集項目（すべて発注書限り・永続しない）。 */
export interface PurchaseOrderHeader {
  /** 発行日 YYYY-MM-DD。既定は当日。 */
  issueDate: string;
  /** 発注番号（自社採番）。 */
  poNumber: string;
  /** 希望納品日 YYYY-MM-DD。 */
  desiredDeliveryDate: string;
  /** 備考・特記事項。 */
  note: string;
}

/**
 * 税額の丸め（backend の `currency.quantize_money` を JS で再現）。
 * 🔴 **通貨の最小単位で丸める**＝旧実装は全通貨を整数へ丸めていた。
 */
function roundTax(amount: number, currency: string): number {
  const factor = 10 ** minorUnits(currency);
  return Math.round(amount * factor) / factor;
}

/**
 * 一時編集（数量変更・行除外）を適用した実効ドラフトを返す。元は変更しない。
 *
 * - 除外された明細は落とす。
 * - 数量は edit.quantity（未指定なら recommended_qty）を使い line_amount を再計算。
 *   実効ドラフトの recommended_qty には最終発注数量を格納する（PDF/CSV へ流すため）。
 * - **税率ごとに**小計・税額を再集計する（旧＝軽減／標準の2本立て固定）。
 *
 * 🔴 **backend の `_build_draft` と同じ規則**＝丸めるのは税額だけ・税率ごとに1回。
 * 明細ごとに丸めると端数が積み上がって仕入先の請求書と合わなくなる。
 * 🔴 **使われていない税率の 0 行は出さない**＝書類に空の行を並べない。
 */
export function applyDraftEdits(
  draft: PurchaseOrderDraft,
  edits: DraftEdits,
): EffectivePurchaseOrderDraft {
  const lines: EffectivePurchaseOrderLine[] = [];
  for (const line of draft.lines) {
    const edit = edits[line.variant_id];
    if (edit?.excluded) continue;
    // 手動注文書では recommended_qty が null。未入力は 0 として扱う。
    const quantity = Math.max(0, edit?.quantity ?? line.recommended_qty ?? 0);
    const unitPrice = Number(line.unit_price);
    const lineAmount = unitPrice * quantity;
    lines.push({
      ...line,
      recommended_qty: quantity,
      line_amount: String(lineAmount),
    });
  }

  // 明細の登場順で税率を束ねる（Map は挿入順を保つ）＝書類の並びは
  // 「その発注書で実際に使われた順」のほうが読み手（仕入先）に自然。
  const groups = new Map<string, TaxGroup>();
  for (const line of lines) {
    const key = `${line.tax_rate_id ?? "none"}|${line.tax_rate_name ?? ""}|${line.tax_rate ?? "0"}`;
    const subtotal = Number(line.line_amount);
    const existing = groups.get(key);
    if (existing) {
      existing.subtotal = String(Number(existing.subtotal) + subtotal);
    } else {
      groups.set(key, {
        tax_rate_id: line.tax_rate_id,
        name: line.tax_rate_name ?? "",
        rate: line.tax_rate ?? "0",
        subtotal: String(subtotal),
        tax: "0",
      });
    }
  }

  // 合計は明細の総和＋税。税グループから合算しない＝下で縮退グループを
  // 出さなくても小計が合計から抜け落ちない（backend の `_build_draft` と同じ形）。
  let total = 0;
  for (const line of lines) {
    total += Number(line.line_amount);
  }
  const taxGroups: TaxGroup[] = [];
  for (const group of groups.values()) {
    // 🔴 税率行が1つも無い店の縮退（id なし・名前なし・率0）はグループにしない
    //    ＝US 等では税セクション自体を出さないのが正しい書類（Step 4/§3）。
    //    マーチャント定義の 0% 行（reverse charge 等）は tax_rate_id を持つ＝残る。
    if (group.tax_rate_id === null && !group.name && Number(group.rate) === 0) {
      continue;
    }
    const subtotal = Number(group.subtotal);
    const tax = roundTax(subtotal * Number(group.rate), draft.currency);
    taxGroups.push({ ...group, tax: String(tax) });
    total += tax;
  }

  return {
    ...draft,
    lines,
    tax_groups: taxGroups,
    total_amount: String(total),
    has_estimated_lines: lines.some((line) => line.is_estimated),
  };
}

/** 発行日を当日既定にしたヘッダを作る。仕入先のPOメモは任意の初期値。 */
export function emptyHeader(today: string, note = ""): PurchaseOrderHeader {
  return { issueDate: today, poNumber: "", desiredDeliveryDate: "", note };
}

/** 一括単価更新1件。`costOverride=null` は手入力値をクリアしデフォルトへ戻す。 */
export interface BulkVariantCostEdit {
  variantId: StockyPublicBigIntId;
  costOverride: number | null;
}

/**
 * `collectBulkCostEdits` の出力。`items` は 1 回の一括 PATCH で送る変更のみ、
 * `unchangedVariantIds` は入力バッファはあるが実効的な変更が無かった（現在値と
 * 同じ）variant で、送信後にバッファを捨てるべきもの。無効値（数値でない・負値）
 * は単発コミット（`commitCost`）と同じく黙って除外し、バッファも残す。
 */
export interface BulkCostEdits {
  items: BulkVariantCostEdit[];
  unchangedVariantIds: StockyPublicBigIntId[];
}

/**
 * 発注書明細の単価入力バッファ（`costInputs`）を、一括単価更新の payload へまとめる。
 *
 * 判定規則は単発の `commitCost` と 1:1 で揃える（片方だけ挙動が変わると、行ごとに
 * 保存したときと「まとめて保存」で結果が食い違うため）:
 *  - 空欄（trim 後空） → override クリア（costOverride=null）。
 *  - 数値でない or 負値 → 無視（items にも unchanged にも入れない＝バッファ据え置き）。
 *  - 現在の unit_price と同値 → 変更なし（unchangedVariantIds へ＝バッファ破棄のみ）。
 *  - それ以外 → costOverride=数値。
 * costInputs にあっても現在の明細に無い variant は対象外（ページ外の残骸を送らない）。
 */
export function collectBulkCostEdits(
  lines: readonly Pick<PurchaseOrderLine, "variant_id" | "unit_price">[],
  costInputs: Readonly<Record<string, string>>,
): BulkCostEdits {
  const lineByVariant = new Map(
    lines.map((line) => [String(line.variant_id), line]),
  );
  const items: BulkVariantCostEdit[] = [];
  const unchangedVariantIds: StockyPublicBigIntId[] = [];
  for (const [key, raw] of Object.entries(costInputs)) {
    const line = lineByVariant.get(key);
    if (line === undefined) continue;
    const variantId = line.variant_id;
    const trimmed = raw.trim();
    if (trimmed === "") {
      items.push({ variantId, costOverride: null });
      continue;
    }
    const n = Number(trimmed);
    if (Number.isNaN(n) || n < 0) continue;
    if (n === Number(line.unit_price)) {
      unchangedVariantIds.push(variantId);
      continue;
    }
    items.push({ variantId, costOverride: n });
  }
  return { items, unchangedVariantIds };
}
